import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useColorModeValue,
  HStack,
  VStack,
  Heading,
  Divider,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Tooltip,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
  FiInfo,
  FiGrid,
  FiClock,
  FiZap,
  FiUser,
  FiTarget,
  FiBarChart2,
  FiDollarSign,
  FiAward
} from 'react-icons/fi';


const ProjectDashboardTable = ({ dashboardData }) => {
  // Theme colors
  const tableBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('linear(to-r, #667eea, #764ba2)', 'linear(to-r, #434190, #5a3d7a)');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const statLabelColor = useColorModeValue('gray.600', 'gray.300');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const infoBg = useColorModeValue('blue.50', 'blue.900');
  const infoColor = useColorModeValue('blue.700', 'blue.200');


  // Safety check
  if (!dashboardData || !dashboardData.dashboardTable || !dashboardData.projects) {
    return <Text>No dashboard data available</Text>;
  }


  const { projects: tableProjects, summary } = dashboardData.dashboardTable;
  const projects = dashboardData.projects; // Full project data with started metrics


  const formatNumber = (num) => {
    // Format with min 0 and max 2 decimal places
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };



  const formatCurrency = (amount, currency = 'AED') => {
    return `${currency} ${formatNumber(amount)}`;
  };

  const isValidCPI = (cpi) => Number.isFinite(cpi);


  // Updated CPI color function based on risk factor table
  const getCPIColor = (cpi) => {
    if (!isValidCPI(cpi)) return 'gray';
    if (cpi >= 0.95) return 'green';
    if (cpi >= 0.90) return 'yellow';
    if (cpi >= 0.85) return 'orange';
    return 'red';
  };


  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case 'LOW': return 'green';
      case 'MEDIUM': return 'yellow';
      case 'HIGH': return 'orange';
      case 'VERY HIGH': return 'red';
      case 'V HIGH': return 'red';
      default: return 'gray';
    }
  };


  // Get CPI text label based on value
  const getCPILabel = (cpi) => {
    if (!isValidCPI(cpi)) return 'N/A';
    if (cpi >= 0.95) return 'GOOD';
    if (cpi >= 0.90) return 'MEDIUM';
    if (cpi >= 0.85) return 'HIGH';
    return 'V HIGH';
  };


  // Get CPI status text
  const getCPIStatus = (cpi) => {
    if (!isValidCPI(cpi)) return 'N/A';
    if (cpi >= 0.95) return 'On Track';
    if (cpi >= 0.90) return 'Medium Risk';
    if (cpi >= 0.85) return 'High Risk';
    return 'Very High Risk';
  };


  // Get CPI color hex for icon
  const getCPIColorHex = (cpi) => {
    if (!isValidCPI(cpi)) return 'gray.500';
    if (cpi >= 0.95) return 'green.500';
    if (cpi >= 0.90) return 'yellow.500';
    if (cpi >= 0.85) return 'orange.500';
    return 'red.500';
  };


  return (
    <VStack spacing={6} align="stretch" w="full">
      {/* Premium Header */}
      


      {/* Info Banner */}
      {/* <Box bg={infoBg} p={3} borderRadius="lg" border="1px" borderColor={borderColor}>
        <HStack spacing={2} mb={1}>
          <Icon as={FiInfo} color={infoColor} boxSize={4} />
          <Text fontWeight="bold" fontSize="xs" color={infoColor}>
            Table Legend:
          </Text>
        </HStack>
        <VStack align="start" spacing={0.5} fontSize="xs" color={infoColor} pl={6}>
          <Text>• <strong>Top values:</strong> All tasks metrics (total project data)</Text>
          <Text>• <strong>Bottom values (Started):</strong> Only tasks with consumed hours &gt; 0 AND installed qty &gt; 0 (used for CPI)</Text>
        </VStack>
      </Box> */}
      {/* Premium Summary Cards - Single Row (6 Cards) */}
      <Box position="relative" zIndex={1}>
        {/* Header Section */}
        <Flex justify="space-between" align="center" mb={6}>
          <HStack spacing={3}>
            {/* <Box
              p={2}
              bg="purple.50"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiActivity} color="purple.600" boxSize={5} />
            </Box>
            <Box>
              <Heading size="md" fontWeight="bold" letterSpacing="tight">
                Overall Summary
              </Heading>
              <Text fontSize="xs" color="gray.500" mt={0.5}>
                Real-time analytics
              </Text>
            </Box> */}
          </HStack>

          <Badge
            colorScheme={getCPIColor(summary.overallCPI)}
            fontSize="xs"
            px={3}
            py={1.5}
            borderRadius="full"
            textTransform="capitalize"
          >
            {getCPIStatus(summary.overallCPI)}
          </Badge>
        </Flex>

        {/* Single Row - 6 Cards */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 6 }} spacing={4}>
          {/* 1. Total Projects - Purple */}
          <Box
            bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
            p={4}
            borderRadius="lg"
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            cursor="pointer"
            _hover={{
              transform: 'translateY(-3px)',
              shadow: '0 12px 30px rgba(102, 126, 234, 0.3)',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1), transparent)',
              borderRadius: 'full',
              pointerEvents: 'none',
            }}
          >
            <Stat position="relative" zIndex={2}>
              <StatLabel fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.7)" mb={2} letterSpacing="0.5px">
                PROJECTS
              </StatLabel>
              <StatNumber fontSize="2xl" fontWeight="900" color="white">
                {summary.totalProjects}
              </StatNumber>
            </Stat>
          </Box>

          {/* 2. Budgeted */}
          <Box
            bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)"
            p={4}
            borderRadius="lg"
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            cursor="pointer"
            _hover={{
              transform: 'translateY(-3px)',
              shadow: '0 12px 30px rgba(102, 126, 234, 0.3)',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1), transparent)',
              borderRadius: 'full',
              pointerEvents: 'none',
            }}
          >
            <Stat position="relative" zIndex={2}>
              <StatLabel fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1} letterSpacing="0.5px">
                BUDGETED
              </StatLabel>
              <HStack spacing={0.5} mb={1}>
                <StatNumber fontSize="lg" fontWeight="900" color="white">
                  {formatNumber(summary.totalBudgetedCost)}
                </StatNumber>
                <Text fontSize="xs" color="rgba(255,255,255,0.8)">
                  AED
                </Text>
              </HStack>
              <Text fontSize="xs" color="rgba(255,255,255,0.6)">
                {formatNumber(summary.totalBudgetedHours)} hrs
              </Text>
            </Stat>
          </Box>

          {/* 3. Consumed */}
          <Box
            bgGradient="linear(135deg, #11998e 0%, #38ef7d 100%)"
            p={4}
            borderRadius="lg"
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            cursor="pointer"
            _hover={{
              transform: 'translateY(-3px)',
              shadow: '0 12px 30px rgba(17, 153, 142, 0.3)',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1), transparent)',
              borderRadius: 'full',
              pointerEvents: 'none',
            }}
          >
            <Stat position="relative" zIndex={2}>
              <StatLabel fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1} letterSpacing="0.5px">
                CONSUMED
              </StatLabel>
              <HStack spacing={0.5} mb={1}>
                <StatNumber fontSize="lg" fontWeight="900" color="white">
                  {formatNumber(summary.totalConsumedCost)}
                </StatNumber>
                <Text fontSize="xs" color="rgba(255,255,255,0.8)">
                  AED
                </Text>
              </HStack>
              <Text fontSize="xs" color="rgba(255,255,255,0.6)">
                {formatNumber(summary.totalConsumedHours)} hrs
              </Text>
            </Stat>
          </Box>

          {/* 4. Earned */}
          <Box
            bgGradient="linear(135deg, #f093fb 0%, #f5576c 100%)"
            p={4}
            borderRadius="lg"
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            cursor="pointer"
            _hover={{
              transform: 'translateY(-3px)',
              shadow: '0 12px 30px rgba(245, 87, 108, 0.3)',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1), transparent)',
              borderRadius: 'full',
              pointerEvents: 'none',
            }}
          >
            <Stat position="relative" zIndex={2}>
              <StatLabel fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1} letterSpacing="0.5px">
                EARNED
              </StatLabel>
              <HStack spacing={0.5} mb={1}>
                <StatNumber fontSize="lg" fontWeight="900" color="white">
                  {formatNumber(summary.totalEarnedCost)}
                </StatNumber>
                <Text fontSize="xs" color="rgba(255,255,255,0.8)">
                  AED
                </Text>
              </HStack>
              <Text fontSize="xs" color="rgba(255,255,255,0.6)">
                {formatNumber(summary.totalEarnedHours)} hrs
              </Text>
            </Stat>
          </Box>

          {/* 5. Variance */}
          <Box
            bgGradient={
              (summary.totalVarianceCost >= 0 && summary.totalVarianceMH >= 0)
                ? "linear(135deg, #fa709a 0%, #fee140 100%)"
                : "linear(135deg, #eb3349 0%, #f45c43 100%)"
            }
            p={4}
            borderRadius="lg"
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            cursor="pointer"
            _hover={{
              transform: 'translateY(-3px)',
              shadow: '0 12px 30px rgba(250, 112, 154, 0.3)',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1), transparent)',
              borderRadius: 'full',
              pointerEvents: 'none',
            }}
          >
            <Stat position="relative" zIndex={2}>
              <StatLabel fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.7)" mb={1} letterSpacing="0.5px">
                VARIANCE
              </StatLabel>
              <HStack spacing={0.5} mb={1}>
                <StatNumber fontSize="lg" fontWeight="900" color="white">
                  {summary.totalVarianceCost >= 0 ? '+' : ''}{formatNumber(Math.abs(summary.totalVarianceCost))}
                </StatNumber>
                <Text fontSize="xs" color="rgba(255,255,255,0.8)">
                  AED
                </Text>
              </HStack>
              <Text fontSize="xs" color="rgba(255,255,255,0.6)">
                {summary.totalVarianceMH >= 0 ? '+' : ''}{formatNumber(Math.abs(summary.totalVarianceMH))} hrs
              </Text>
            </Stat>
          </Box>

          {/* 6. CPI */}
          <Box
            bgGradient={
              !isValidCPI(summary.overallCPI)
                ? "linear(135deg, #6b7280 0%, #4b5563 100%)"
                : summary.overallCPI >= 0.95
                ? "linear(135deg, #10b981 0%, #059669 100%)"
                : summary.overallCPI >= 0.90
                  ? "linear(135deg, #f59e0b 0%, #d97706 100%)"
                  : summary.overallCPI >= 0.85
                    ? "linear(135deg, #f97316 0%, #ea580c 100%)"
                    : "linear(135deg, #ef4444 0%, #dc2626 100%)"
            }
            p={4}
            borderRadius="lg"
            position="relative"
            overflow="hidden"
            transition="all 0.3s"
            cursor="pointer"
            _hover={{
              transform: 'translateY(-3px)',
              shadow: !isValidCPI(summary.overallCPI)
                ? '0 12px 30px rgba(107, 114, 128, 0.3)'
                : summary.overallCPI >= 0.95
                ? '0 12px 30px rgba(16, 185, 129, 0.3)'
                : summary.overallCPI >= 0.90
                  ? '0 12px 30px rgba(245, 158, 11, 0.3)'
                  : summary.overallCPI >= 0.85
                    ? '0 12px 30px rgba(249, 115, 22, 0.3)'
                    : '0 12px 30px rgba(239, 68, 68, 0.3)',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.15), transparent)',
              borderRadius: 'full',
              pointerEvents: 'none',
            }}
          >
            <Stat position="relative" zIndex={2}>
              <HStack justify="space-between" mb={1}>
                <StatLabel fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.8)" letterSpacing="0.5px">
                  CPI
                </StatLabel>
                <Badge
                  bg="rgba(255,255,255,0.25)"
                  color="white"
                  fontSize="xs"
                  fontWeight="bold"
                  px={1.5}
                  py={0.5}
                  borderRadius="full"
                  textTransform="uppercase"
                  backdropFilter="blur(10px)"
                >
                  {!isValidCPI(summary.overallCPI)
                    ? 'N/A'
                    : summary.overallCPI >= 0.95
                    ? 'OK'
                    : summary.overallCPI >= 0.90
                      ? 'MED'
                      : summary.overallCPI >= 0.85
                        ? 'HIGH'
                        : 'VHIGH'}
                </Badge>
              </HStack>
              <StatNumber fontSize="2xl" fontWeight="900" color="white">
                {isValidCPI(summary.overallCPI) ? summary.overallCPI.toFixed(3) : 'N/A'}
              </StatNumber>
            </Stat>
          </Box>
        </SimpleGrid>
      </Box>





      {/* Main Table */}
      <Box
        overflowX="auto"
        bg={tableBg}
        borderRadius="xl"
        shadow="2xl"
        border="1px"
        borderColor={borderColor}
      >
        <Table variant="simple" size="sm">
          <Thead bgGradient={headerBg}>
            <Tr>
              <Th color="white" borderColor="whiteAlpha.300">PROJECT</Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>PROGRESS</Th>
              {/* <Th color="white" borderColor="whiteAlpha.300" isNumeric>
                <Tooltip label="Total planned quantity vs installed quantity">
                  QUANTITY
                </Tooltip>
              </Th> */}

              {/* Manhours Section */}
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>
                <Tooltip label="All tasks budgeted hours (Started tasks shown below)">
                  BUDGETED HRS
                </Tooltip>
              </Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>
                <Tooltip label="All tasks consumed hours (Started tasks shown below)">
                  CONSUMED HRS
                </Tooltip>
              </Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>
                <Tooltip label="All tasks: Installed Qty × Productivity | Started: Progress % × Budgeted (used for CPI)">
                  EARNED HRS
                </Tooltip>
              </Th>

              {/* Cost Section */}
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>BUDGETED COST</Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>CONSUMED COST</Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>EARNED COST</Th>

              {/* Performance Metrics */}
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>
                <Tooltip label="Cost Performance Index = Earned Value / Consumed Hours">
                  CPI
                </Tooltip>
              </Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>VAR. MH</Th>
              <Th color="white" borderColor="whiteAlpha.300" isNumeric>VAR. COST</Th>
              <Th color="white" borderColor="whiteAlpha.300">RISK</Th>
            </Tr>
          </Thead>
          <Tbody>
            {tableProjects.map((project) => {
              console.log('project p[rint',project);
              // Find matching full project data
              const fullProject = projects.find(p => p.id === project.id);
              const projectCPI = project.totalConsumedHours > 0
                ? project.totalEarnedHours / project.totalConsumedHours
                : null;


              return (
                <Tr
                  key={project.id}
                  _hover={{ bg: hoverBg, transform: 'scale(1.005)', transition: 'all 0.2s' }}
                >
                  <Td borderColor={borderColor} fontWeight="bold" fontSize="sm">
                    {project.name}
                  </Td>
                  <Td borderColor={borderColor} isNumeric>
                    <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                      {(project.progressOfWorks * 100).toFixed(1)}%
                    </Badge>
                  </Td>


                  {/* Quantity - ALL + STARTED */}
                  {/* <Td borderColor={borderColor} isNumeric>
                    <VStack spacing={0} align="end">
                      {fullProject && (
                        <>
                          <Tooltip label={`${formatNumber(fullProject.totalInstalledQuantity)} installed out of ${formatNumber(fullProject.totalQuantity)} total`}>
                            <Text fontSize="xs" >
                              {formatNumber(fullProject.totalInstalledQuantity)} / {formatNumber(fullProject.totalQuantity)}
                            </Text>
                          </Tooltip>
                          <Tooltip label="Started tasks quantity progress">
                            <Text fontSize="xs" color="green.600" >
                              Std: {formatNumber(fullProject.startedInstalledQuantity)} / {formatNumber(fullProject.startedTotalQuantity)}
                            </Text>
                          </Tooltip>
                        </>
                      )}
                    </VStack>
                  </Td> */}

                  {/* Budgeted Hours - ALL + STARTED */}
                  <Td borderColor={borderColor} isNumeric>
                    <VStack spacing={0} align="end">
                      <Text fontSize="sm" fontWeight="semibold">
                        {formatNumber(project.totalBudgetedHours)}
                      </Text>
                      {fullProject && (
                        <Text fontSize="xs" color="green.600" fontWeight="medium">
                          Std: {formatNumber(fullProject.startedBudgetedManhours)}
                        </Text>
                      )}
                    </VStack>
                  </Td>


                  {/* Consumed Hours - ALL + STARTED */}
                  <Td borderColor={borderColor} isNumeric>
                    <VStack spacing={0} align="end">
                      <Text fontSize="sm" fontWeight="semibold">
                        {formatNumber(project.totalConsumedHours)}
                      </Text>

                    </VStack>
                  </Td>


                  {/* Earned Hours - ALL + STARTED EV */}
                  <Td borderColor={borderColor} isNumeric>
                    <VStack spacing={0} align="end">
                      <Tooltip label="All tasks: Installed Qty × Productivity">
                        <Text fontSize="sm" fontWeight="semibold" color="purple.600">
                          {formatNumber(project.totalEarnedHours)}
                        </Text>
                      </Tooltip>
                      {fullProject && (
                        <Tooltip label="Earned manhours">
                          <Text fontSize="xs" color="green.600" fontWeight="bold">
                            {/* Std EV: {formatNumber(fullProject.startedEarnedValue)} */}
                          </Text>
                        </Tooltip>
                      )}
                    </VStack>
                  </Td>

                  {/* Cost Data */}
                  <Td borderColor={borderColor} isNumeric fontSize="xs">
                    {formatNumber(project.totalBudgetedCost)}
                  </Td>
                  <Td borderColor={borderColor} isNumeric fontSize="xs">
                    {formatNumber(project.totalConsumedCost)}
                  </Td>
                  <Td borderColor={borderColor} isNumeric fontSize="xs">
                    {formatNumber(project.totalEarnedCost)}
                    
                  </Td>

                  {/* Performance Metrics - Updated CPI Logic */}
                  <Td borderColor={borderColor} isNumeric>
                    <VStack spacing={0} align="end">
                      <HStack spacing={1} justify="flex-end">
                        <Icon
                          as={isValidCPI(projectCPI) && projectCPI >= 0.95 ? FiTrendingUp : FiTrendingDown}
                          color={getCPIColorHex(projectCPI)}
                          boxSize={3}
                        />
                        <Text
                          fontWeight="bold"
                          fontSize="md"
                          color={getCPIColorHex(projectCPI)}
                        >
                          {isValidCPI(projectCPI) ? projectCPI.toFixed(2) : 'N/A'}
                        </Text>
                      </HStack>
                      {fullProject && (
                        <Text fontSize="xs" color={statLabelColor}>
                          {fullProject.startedTasksCount} tasks
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td borderColor={borderColor} isNumeric>
                    <Text
                      fontSize="xs"
                      color={project.varianceMH >= 0 ? 'green.500' : 'red.500'}
                      fontWeight="semibold"
                    >
                      {project.varianceMH >= 0 ? '+' : ''}{formatNumber(Math.abs(project.varianceMH))}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor} isNumeric>
                    <Text
                      fontSize="xs"
                      color={project.varianceCost >= 0 ? 'green.500' : 'red.500'}
                      fontWeight="semibold"
                    >
                      {project.varianceCost >= 0 ? '+' : ''}{formatNumber(Math.abs(project.varianceCost))}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Badge
                      colorScheme={getRiskBadgeColor(project.riskLevel)}
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontWeight="bold"
                    >
                      {project.riskLevel}
                    </Badge>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>


      <Divider />





      {/* Risk Legend - Updated */}
      <Box
        bg={cardBg}
        p={4}
        borderRadius="lg"
        shadow="md"
        border="1px"
        borderColor={borderColor}
      >
        <Text fontSize="xs" fontWeight="bold" mb={3} color={textColor}>
          CPI Risk Factor Levels:
        </Text>
        <Flex gap={3} flexWrap="wrap">
          <Badge colorScheme="green" fontSize="xs" px={3} py={1} borderRadius="full">
            ≥ 0.95 = LOW
          </Badge>
          <Badge colorScheme="yellow" fontSize="xs" px={3} py={1} borderRadius="full">
            0.90-0.95 = MEDIUM
          </Badge>
          <Badge colorScheme="orange" fontSize="xs" px={3} py={1} borderRadius="full">
            0.85-0.90 = HIGH
          </Badge>
          <Badge colorScheme="red" fontSize="xs" px={3} py={1} borderRadius="full">
            &lt; 0.85 = V HIGH
          </Badge>
        </Flex>
      </Box>
    </VStack>
  );
};


export default ProjectDashboardTable;
